import { formatLeadFormFields } from "./formatLeadFormFields";
import { formatLeadMarkdown } from "./formatLeadMarkdown";

function cleanFields(fields) {
  return Object.fromEntries(
    Object.entries(fields).filter(([, value]) => value !== undefined && value !== "")
  );
}

function getFormspreeId(payload) {
  if (payload.source === "email-lead") {
    return (
      process.env.REACT_APP_FORMSPREE_EMAIL_LEAD ||
      process.env.REACT_APP_FORMSPREE_ID ||
      ""
    );
  }
  return (
    process.env.REACT_APP_FORMSPREE_SELECTOR ||
    process.env.REACT_APP_FORMSPREE_ID ||
    ""
  );
}

function getLeadApiBase() {
  const base = process.env.REACT_APP_LEAD_API_URL || "";
  return base.replace(/\/$/, "");
}

async function submitToPushPlus(token, payload) {
  const { title, content } = formatLeadMarkdown(payload);

  const response = await fetch("https://www.pushplus.plus/send", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      token,
      title,
      content,
      template: "markdown",
    }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok || data.code !== 200) {
    throw new Error(data.msg || "Submit failed");
  }

  return { ok: true, mode: "pushplus" };
}

async function submitToServerChan(sendKey, payload) {
  const { title, content } = formatLeadMarkdown(payload);

  const response = await fetch(`https://sctapi.ftqq.com/${sendKey}.send`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      title,
      desp: content,
    }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok || data.code !== 0) {
    throw new Error(data.message || data.msg || "Submit failed");
  }

  return { ok: true, mode: "serverchan" };
}

async function submitToFormSubmit(email, payload) {
  const fields = cleanFields(formatLeadFormFields(payload));

  const response = await fetch(
    `https://formsubmit.co/ajax/${encodeURIComponent(email)}`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...fields,
        _template: "table",
        _captcha: "false",
      }),
    }
  );

  const data = await response.json().catch(() => ({}));

  if (!response.ok || (data.success !== "true" && data.success !== true)) {
    throw new Error(data.message || "Submit failed");
  }

  return { ok: true, mode: "formsubmit" };
}

async function submitToWeb3Forms(accessKey, payload) {
  const fields = cleanFields(formatLeadFormFields(payload));
  const { _subject, _replyto, ...rest } = fields;

  const response = await fetch("https://api.web3forms.com/submit", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      access_key: accessKey,
      subject: _subject || "官网新询盘",
      email: _replyto,
      from_name: "咏远官网",
      ...rest,
    }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok || !data.success) {
    throw new Error(data.message || "Submit failed");
  }

  return { ok: true, mode: "web3forms" };
}

async function submitToFormspree(formId, payload) {
  const fields = cleanFields(formatLeadFormFields(payload));

  const response = await fetch(`https://formspree.io/f/${formId}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(fields),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || "Submit failed");
  }

  return { ok: true, mode: "formspree" };
}

async function submitToLeadApi(payload) {
  const apiBase = getLeadApiBase();
  const url = apiBase ? `${apiBase}/api/leads` : "/api/leads";
  const headers = { "Content-Type": "application/json" };

  const secret = process.env.REACT_APP_LEAD_API_SECRET;
  if (secret) {
    headers["X-Lead-Secret"] = secret;
  }

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || "Submit failed");
  }

  return data;
}

export async function submitLead(payload) {
  const enriched = {
    ...payload,
    pageUrl: window.location.href,
    submittedAt: new Date().toISOString(),
  };

  const pushPlusToken = process.env.REACT_APP_PUSHPLUS_TOKEN;
  if (pushPlusToken) {
    return submitToPushPlus(pushPlusToken, enriched);
  }

  const serverChanKey = process.env.REACT_APP_SERVERCHAN_SENDKEY;
  if (serverChanKey) {
    return submitToServerChan(serverChanKey, enriched);
  }

  const formsubmitEmail = process.env.REACT_APP_FORMSUBMIT_EMAIL;
  if (formsubmitEmail) {
    return submitToFormSubmit(formsubmitEmail, enriched);
  }

  if (getLeadApiBase()) {
    return submitToLeadApi(enriched);
  }

  const web3formsKey = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;
  if (web3formsKey) {
    return submitToWeb3Forms(web3formsKey, enriched);
  }

  const formspreeId = getFormspreeId(enriched);
  if (formspreeId) {
    return submitToFormspree(formspreeId, enriched);
  }

  if (process.env.NODE_ENV === "development") {
    return submitToLeadApi(enriched);
  }

  throw new Error("Lead submission is not configured");
}
